import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { NewsletterEdition, NewsletterEditionStatus } from "../types/newsletterEdition";
import { logout } from "../services/authService";
import campaignService from "../services/campaignService";
import SidePanel from "./SidePanel";
import EditionLinks from "./EditionLinks";
import CustomContentForm from "./CustomContentForm";
import OnboardingWalkthrough from "./Onboarding/steps/OnboardingWalkthrough";
import NewsletterStatusPill from "./NewsletterStatusPill";
import NewsletterApprovalConfirmModal from "./NewsletterApprovalConfirmModal";
import { Mail, MailOpen, Calendar, SlidersHorizontal, MoreHorizontal, BarChart3 } from "lucide-react";

/**
 * Call the API to fetch all newsletter editions that belong to the current user.
 */
const fetchNewsletterEditions = async (): Promise<NewsletterEdition[]> => {
  return campaignService.listNewsletterEditions();
};

const Posts: React.FC = () => {
  const navigate = useNavigate();
  const [newsletterEditions, setNewsletterEditions] = useState<
    NewsletterEdition[]
  >([]);

  const [isLinksSidePanelOpen, setIsLinksSidePanelOpen] = useState(false);
  const [isTextSidePanelOpen, setIsTextSidePanelOpen] = useState(false);
  const [activeEditionId, setActiveEditionId] = useState("");

  const POSTS_PER_PAGE = 25;
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (currentPage > 1) {
      newParams.set("page", currentPage.toString());
    } else {
      newParams.delete("page");
    }
    setSearchParams(newParams, { replace: true });
  }, [currentPage]);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuId]);

  const copyNewsletterEdition = async (newsletterEditionId: string): Promise<void> => {
    const result = await campaignService.copyNewsletterEdition(newsletterEditionId);
    navigate(`/newsletterEdition/${result.id}`);
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [viewMode, setViewMode] = useState<"active" | "all">("active");

  const ALL_STATUSES = ["Preparing", "Finalizing", "Awaiting Approval", "Approved", "Sending", "Published", "Expired"];

  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  const hasActiveFilters = statusFilter !== "" || dateFrom !== "" || dateTo !== "";

  const clearFilters = () => {
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  const [approvalTargetEdition, setApprovalTargetEdition] = useState<NewsletterEdition | null>(null);

  const handleUpdateNewsletterStatus = async (
    newsletterEditionId: string,
    status: NewsletterEditionStatus,
  ): Promise<void> => {
    const toggledStatus = campaignService.getToggledStatus(status);
    if (!toggledStatus) return;

    if (toggledStatus === NewsletterEditionStatus.Approved) {
      const edition = newsletterEditions.find(e => String(e.id) === String(newsletterEditionId));
      if (edition) {
        setApprovalTargetEdition(edition);
        return;
      }
    }

    await campaignService.updateNewsletterEditionStatus(newsletterEditionId, toggledStatus);
    fetchNewsletterEditions().then(setNewsletterEditions);
  };

  const handleApprovalConfirm = async (selectedSendDate?: string): Promise<void> => {
    if (!approvalTargetEdition) return;
    try {
      if (selectedSendDate) {
        await campaignService.updateNewsletterEditionScheduledSendDate(
          approvalTargetEdition.id, selectedSendDate);
      }
      await campaignService.updateNewsletterEditionStatus(
        approvalTargetEdition.id, NewsletterEditionStatus.Approved);
      setApprovalTargetEdition(null);
      fetchNewsletterEditions().then(setNewsletterEditions);
    } catch (error) {
      console.error("Error approving newsletter edition:", error);
    }
  };

  const deleteNewsletterEdition = async (
    newsletterEditionId: string,
  ): Promise<any> => {
    await campaignService.deleteNewsletterEdition(newsletterEditionId);
    // fetch the updated list of newsletter editions after deletion
    fetchNewsletterEditions().then(setNewsletterEditions);
  };

  useEffect(() => {
    fetchNewsletterEditions()
      .then(setNewsletterEditions)
      .catch((error) => {
        console.error("Error fetching newsletter editions:", error);

        if (error.status === 401) {
          logout().finally(() => navigate("/login?status=timeout"));
        }
      });
  }, []);

  const [submitCustomContentSuccess, setSubmitCustomContentSuccess] = useState(false);
  const handleSubmitCustomContentSuccess = (success: boolean) => {
    if (success) {
        setSubmitCustomContentSuccess(true);

        // clear success banner so it's not sticky
        setTimeout(() => {
            setSubmitCustomContentSuccess(false);
        }, 2000);
    } else {
        setSubmitCustomContentSuccess(false);
    }
  }

  const location = useLocation();
  const onboardingStatus = location.state?.onboardingStatus;
  const [showWalkthrough, setShowWalkthrough] = useState(onboardingStatus === "complete");

  // Filter and sort editions by newest first
  const sortedEditions = useMemo(() => {
    let filtered = [...newsletterEditions];

    // In "active" view, hide Preparing unless a specific status filter overrides
    if (viewMode === "active" && !statusFilter) {
      filtered = filtered.filter(e => e.status !== "Preparing");
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    // Apply date range filter (on scheduled_send_start_date)
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter(e => {
        const d = e.scheduled_send_start_date ? new Date(e.scheduled_send_start_date) : new Date(e.created_at);
        return d >= from;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(e => {
        const d = e.scheduled_send_start_date ? new Date(e.scheduled_send_start_date) : new Date(e.created_at);
        return d <= to;
      });
    }

    // Sort by selected order
    filtered.sort((a, b) => {
      const dateA = new Date(a.scheduled_send_start_date || a.created_at);
      const dateB = new Date(b.scheduled_send_start_date || b.created_at);
      return sortOrder === "newest"
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    });

    return filtered;
  }, [newsletterEditions, statusFilter, dateFrom, dateTo, sortOrder, viewMode]);

  const totalPages = Math.max(1, Math.ceil(sortedEditions.length / POSTS_PER_PAGE));
  const paginatedEditions = sortedEditions.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const getVisiblePages = () => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = startPage + maxVisible - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = endPage - maxVisible + 1;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  return (
      <div>
          <div
              style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
              }}>
              <h1 style={{display: "inline-block"}}>Posts</h1>

              <div style={{display: "inline-block", marginLeft: "auto"}}>
                  <button
                      type="submit"
                      className="primary-button"
                      onClick={() => navigate("/create-post")}
                  >
                      Create new post
                  </button>
              </div>
          </div>
          <p style={{marginTop: "0"}}>
              Create new posts, view existing posts, and edit templates.
          </p>

          { submitCustomContentSuccess &&
            <div
                style={{
                    backgroundColor: "#e0f7df",
                    color: "#2e7d32",
                    border: "1px solid #2e7d32",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    marginBottom: "12px",
                    maxWidth: "500px",
                }}
            >
                ✅
                <span style={{marginLeft: "12px"}}>Custom content submitted!</span>
            </div>
          }


          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ display: "flex", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden" }}>
                <button
                  onClick={() => { setViewMode("active"); setCurrentPage(1); }}
                  style={{
                    padding: "6px 14px",
                    border: "none",
                    borderRight: "1px solid #d1d5db",
                    backgroundColor: viewMode === "active" ? "#f3f4f6" : "#fff",
                    fontWeight: viewMode === "active" ? 600 : 400,
                    cursor: "pointer",
                    fontSize: "13px",
                    color: viewMode === "active" ? "#111827" : "#6b7280",
                  }}
                >
                  Active
                </button>
                <button
                  onClick={() => { setViewMode("all"); setCurrentPage(1); }}
                  style={{
                    padding: "6px 14px",
                    border: "none",
                    backgroundColor: viewMode === "all" ? "#f3f4f6" : "#fff",
                    fontWeight: viewMode === "all" ? 600 : 400,
                    cursor: "pointer",
                    fontSize: "13px",
                    color: viewMode === "all" ? "#111827" : "#6b7280",
                  }}
                >
                  All posts
                </button>
              </div>
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                Showing {paginatedEditions.length} of {sortedEditions.length} posts
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 14px",
                  border: hasActiveFilters ? "1px solid #5d5ded" : "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: hasActiveFilters ? "#f0f0ff" : "#fff",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: hasActiveFilters ? "#5d5ded" : "#374151",
                  fontWeight: 500,
                }}
              >
                <SlidersHorizontal size={16} />
                Filter
              </button>
              {isFilterOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    zIndex: 10,
                    width: "260px",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "15px", marginBottom: "12px" }}>Filter</div>

                  <label style={{ fontSize: "13px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "14px",
                      marginBottom: "12px",
                    }}
                  >
                    <option value="">All statuses</option>
                    {ALL_STATUSES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  <label style={{ fontSize: "13px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Publish date</label>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                      style={{ flex: 1, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "13px" }}
                    />
                    <span style={{ color: "#9ca3af" }}>-</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                      style={{ flex: 1, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "13px" }}
                    />
                  </div>

                  <button
                    onClick={clearFilters}
                    style={{
                      width: "100%",
                      padding: "6px",
                      border: "none",
                      backgroundColor: "transparent",
                      color: "#6b7280",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            <select
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value as "newest" | "oldest"); setCurrentPage(1); }}
              style={{
                padding: "8px 14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "#fff",
                fontSize: "14px",
                color: "#374151",
                fontWeight: 500,
                cursor: "pointer",
                appearance: "auto",
              }}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            </div>
          </div>

          <ul className="newsletter-list">
              {paginatedEditions.map(newsletterEdition => (
                  <li
                      id="walkthrough-newsletter-edition"
                      key={newsletterEdition.id}
                      className="newsletter-item"
                      style={{ position: 'relative', zIndex: openMenuId === newsletterEdition.id ? 10 : 0 }}>

                      <Link
                          to={`/newsletterEdition/${newsletterEdition.id}`}
                          style={{
                              textDecoration: 'none',
                              color: 'inherit',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                              padding: 'inherit'
                          }}>
                          <div className="newsletter-item-content-left">
                            <span className="newsletter-title">
                                {newsletterEdition.title}
                            </span>
                          </div>
                      </Link>

                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        position: 'absolute',
                        right: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        paddingRight: '16px'
                      }}>

                        {newsletterEdition.status === "Published" && newsletterEdition.total_deliveries != null && (
                          <>
                            <span
                              className="newsletter-status"
                              style={{backgroundColor: "#e5e7eb", color: "#374151"}}>
                              <span className="newsletter-status-icon">
                                <Mail size={16} />
                              </span>
                              <span>{newsletterEdition.total_deliveries.toLocaleString()} Deliveries</span>
                            </span>

                            <span
                              className="newsletter-status"
                              style={{backgroundColor: "#ccfbf1", color: "#0f766e"}}>
                              <span className="newsletter-status-icon">
                                <MailOpen size={16} />
                              </span>
                              <span>{newsletterEdition.open_rate}% Open Rate</span>
                            </span>
                          </>
                        )}

                        {newsletterEdition.scheduled_send_start_date && (
                          <span
                            className="newsletter-status"
                            style={{backgroundColor: "#dbeafe", color: "#1d4ed8"}}>
                            <span className="newsletter-status-icon">
                              <Calendar size={16} />
                            </span>
                            <span>{new Date(newsletterEdition.scheduled_send_start_date).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric'
                            })}</span>
                          </span>
                        )}

                        <NewsletterStatusPill
                            status={newsletterEdition.status}
                            handleUpdate={() => handleUpdateNewsletterStatus(newsletterEdition.id, newsletterEdition.status)}
                        />

                        <div style={{ position: "relative" }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setOpenMenuId(openMenuId === newsletterEdition.id ? null : newsletterEdition.id);
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              color: "#6b7280",
                            }}
                          >
                            <MoreHorizontal size={18} />
                          </button>

                          {openMenuId === newsletterEdition.id && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: "absolute",
                                top: "calc(100% + 4px)",
                                right: 0,
                                backgroundColor: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                zIndex: 20,
                                minWidth: "180px",
                                padding: "4px 0",
                              }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setOpenMenuId(null);
                                  copyNewsletterEdition(newsletterEdition.id);
                                }}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  padding: "8px 16px",
                                  background: "none",
                                  border: "none",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  color: "#111827",
                                }}
                              >
                                Copy to new
                              </button>

                              {(() => {
                                const isSentStatus = newsletterEdition.status === NewsletterEditionStatus.Published || newsletterEdition.status === NewsletterEditionStatus.Sending;
                                const sendDate = newsletterEdition.scheduled_send_start_date
                                  ? new Date(newsletterEdition.scheduled_send_start_date)
                                  : null;
                                const isOldEnough = sendDate && (Date.now() - sendDate.getTime() > 24 * 60 * 60 * 1000);
                                const isEnabled = isSentStatus && isOldEnough;

                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      if (isEnabled) {
                                        setOpenMenuId(null);
                                        navigate(`/editions/${newsletterEdition.id}/analytics`);
                                      }
                                    }}
                                    title={
                                      !isSentStatus
                                        ? "Only available for sent editions"
                                        : !isOldEnough
                                        ? "Analytics available 24 hours after sending"
                                        : undefined
                                    }
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      width: "100%",
                                      padding: "8px 16px",
                                      background: "none",
                                      border: "none",
                                      textAlign: "left",
                                      cursor: isEnabled ? "pointer" : "default",
                                      fontSize: "14px",
                                      color: isEnabled ? "#111827" : "#9ca3af",
                                    }}
                                  >
                                    <BarChart3 size={14} />
                                    View Analytics
                                  </button>
                                );
                              })()}

                              {newsletterEdition.onboarding_blueprint_id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setOpenMenuId(null);
                                    navigate(`/blueprints/${newsletterEdition.onboarding_blueprint_id}`);
                                  }}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "8px 16px",
                                    background: "none",
                                    border: "none",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    color: "#111827",
                                  }}
                                >
                                  Go to blueprint
                                </button>
                              )}

                              <div style={{ height: "1px", backgroundColor: "#e5e7eb", margin: "4px 0" }} />

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setOpenMenuId(null);
                                  const confirmed = window.confirm(
                                    "Are you sure you want to delete this post? This action cannot be undone."
                                  );
                                  if (confirmed) {
                                    deleteNewsletterEdition(newsletterEdition.id);
                                  }
                                }}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  padding: "8px 16px",
                                  background: "none",
                                  border: "none",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  color: "#dc2626",
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                  </li>
              ))}
          </ul>

          {totalPages > 1 && (
            <div style={{ textAlign: "center", margin: "20px 0" }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="subscriber-pagination-end-button"
                style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                &lt;&lt;
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="subscriber-pagination-end-button"
                style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                &lt;
              </button>
              {getVisiblePages().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className="subscriber-pagination-number-button"
                  style={{
                    backgroundColor: currentPage === pageNum ? "#5d5ded" : "#fff",
                    color: currentPage === pageNum ? "#fff" : "#5d5ded",
                  }}
                >
                  {pageNum}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="subscriber-pagination-end-button"
                style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
              >
                &gt;
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="subscriber-pagination-end-button"
                style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
              >
                &gt;&gt;
              </button>
            </div>
          )}

          <SidePanel isOpen={isLinksSidePanelOpen} onClose={() => setIsLinksSidePanelOpen(false)}>
              <EditionLinks newsletterEditionId={activeEditionId}/>
          </SidePanel>

          <SidePanel isOpen={isTextSidePanelOpen} onClose={() => setIsTextSidePanelOpen(false)}>
              <CustomContentForm
                newsletterEditionId={activeEditionId}
                setSidePanelOpen={setIsTextSidePanelOpen}
                setSubmitSuccess={handleSubmitCustomContentSuccess}
              />
          </SidePanel>

          <OnboardingWalkthrough run={showWalkthrough} setRun={setShowWalkthrough} />

          {approvalTargetEdition && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1000,
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(0, 0, 0, 0.15)",
              }}
            >
              <NewsletterApprovalConfirmModal
                editionId={approvalTargetEdition.id}
                newsletterTitle={approvalTargetEdition.title}
                scheduledSendDate={approvalTargetEdition.scheduled_send_start_date}
                allEditions={newsletterEditions}
                onConfirm={handleApprovalConfirm}
                onCancel={() => setApprovalTargetEdition(null)}
              />
            </div>
          )}
      </div>
  );
};

export default Posts;
